/**
 * Tab ↔ daemon wire protocol of the local bridge (ADR-009 §1.1, phase 3).
 *
 * Both ends import this module: the tab's BridgeClient (browser) and the
 * `maipo-bridged` daemon (Node ≥ 20). Key derivation and the challenge
 * response use WebCrypto (`globalThis.crypto.subtle`), which both runtimes
 * provide, so there is one implementation of the authentication maths.
 *
 * Pairing: `maipo-mcp pair` makes a single-use 256-bit token and a pairing
 * id. The daemon opens `https://maipo.kua.cl/#agent-pair=<token>&port=47651
 * &pairing=<id>&client=…&scopes=…&folder=…`. The fragment never reaches a
 * server. After the user clicks Allow, both sides derive the same pairing key
 * with HKDF-SHA256 from the token, the pairing id and the tab origin. The tab
 * then proves possession with an HMAC over the daemon's nonce; neither the
 * token nor the key ever crosses the socket. A reconnect is the same
 * challenge-response against the stored key.
 */

/**
 * Newest wire protocol version. 0.2 adds staged assets: the daemon sends
 * image bytes for a call to the tab (a JSON `stage` message, then binary
 * chunks) before the call itself. 0.1 tabs never receive binary frames.
 */
import type { StagedAssetManifest } from './assets';

export const BRIDGE_PROTOCOL_VERSION = '0.2' as const;
/** Every version either end speaks, newest first. The tab answers the daemon's hello with the newest one both know. */
export const BRIDGE_PROTOCOL_VERSIONS: readonly string[] = Object.freeze(['0.2', '0.1']);
/** The first version in which the daemon may stage assets. */
export const BRIDGE_STAGING_VERSION = '0.2' as const;
/** Whether a negotiated protocol version carries staged assets. */
export const supportsStaging = (version: string | null | undefined): boolean => version === '0.2';
/** Fixed; the tab's CSP names exactly this port. */
export const BRIDGE_PORT = 47651;
export const BRIDGE_PATH = '/tab';
export const BRIDGE_PRODUCTION_ORIGIN = 'https://maipo.kua.cl';
/** Accepted by the daemon only when it runs in dev mode. */
export const BRIDGE_DEV_ORIGIN = 'http://localhost:5173';
export const BRIDGE_HOSTS = Object.freeze([`127.0.0.1:${BRIDGE_PORT}`, `[::1]:${BRIDGE_PORT}`, `localhost:${BRIDGE_PORT}`]);

export const BRIDGE_LIMITS = Object.freeze({
  /** Largest JSON (text) frame either side accepts. */
  jsonFrameBytes: 1024 * 1024,
  /** Largest binary frame either side accepts (tab → daemon: exports, previews; daemon → tab: staged asset chunks). */
  binaryFrameBytes: 16 * 1024 * 1024,
  /** A socket that has not authenticated within this time is closed. */
  authTimeoutMs: 2_000,
  /** Authentication failures per rolling minute before a ban. */
  authFailuresPerMinute: 5,
  banMs: 60_000,
  /** A pairing link is valid this long. */
  pairingTokenTtlMs: 10 * 60_000,
  maxFolders: 8,
  maxFolderLength: 1024,
  /** Staged assets (protocol 0.2), ADR-009 §5.1 rule 3. */
  staging: Object.freeze({
    /** `{ inlineBase64 }`: decoded bytes per asset. */
    inlineAssetBytes: 10 * 1024 * 1024,
    /** `{ localPath }`: bytes per file read from an import folder. */
    localAssetBytes: 32 * 1024 * 1024,
    /** Every staged byte of one call together (distinct assets; the daemon may be configured lower, never higher). */
    callBytes: 64 * 1024 * 1024,
    /** Distinct assets per call. */
    callAssets: 16,
    /** Staged bytes held at once across all outstanding calls of one connection. */
    inFlightBytes: 128 * 1024 * 1024,
    /** Payload bytes per binary chunk (below binaryFrameBytes with room for the header). */
    chunkBytes: 8 * 1024 * 1024,
    /** A staged call whose bytes and call message have not all arrived by then is dropped by the tab. */
    ttlMs: 60_000,
  }),
});

/**
 * Scopes a pairing can hold (ADR-009 §5.2). The tab enforces them; the daemon
 * mirrors them as defence in depth.
 */
export const BRIDGE_SCOPES = Object.freeze([
  'read:structure',
  'read:text',
  'read:preview',
  'read:preview:full',
  'write:document',
  'write:locks',
  'export:local',
  'export:return',
  'asset:import',
] as const);
export type BridgeScope = typeof BRIDGE_SCOPES[number];
/** What `maipo-mcp pair` asks for unless told otherwise. */
export const DEFAULT_PAIRING_SCOPES: readonly BridgeScope[] = Object.freeze(['read:structure', 'read:text', 'read:preview', 'write:document', 'export:local']);
/** Scopes the Allow dialog explains in plain words. */
export const SCOPE_DESCRIPTIONS: Readonly<Record<BridgeScope, string>> = Object.freeze({
  'read:structure': 'See layer names, kinds, positions and styles',
  'read:text': 'Read the words in text layers',
  'read:preview': 'See small previews (at most 1024 px for the whole document)',
  'read:preview:full': 'See full-resolution previews',
  'write:document': 'Edit open documents (every change is one undoable step)',
  'write:locks': 'Change layer locks',
  'export:local': 'Save full-resolution exports (PNG, JPEG, public scene JSON with its images) into the folders listed below, where other programs on this computer can read them',
  'export:return': 'Receive exported files directly (asks each time)',
  'asset:import': 'Read image files (PNG, JPEG, WebP) in the import folders listed below and place them in open documents',
});

/** Bridge tool names: the phase 2 page-API methods plus the phase 3 additions. */
export const BRIDGE_TOOLS = Object.freeze([
  'listDocuments', 'getCapabilities', 'listCommands', 'inspect', 'getLayer', 'apply', 'undo', 'redo', 'revertTransaction', 'renderPreview', 'export', 'publish',
  // Documents (agent parity phase 2).
  'newDocument', 'closeDocument', 'copyLayer',
] as const);
export type BridgeTool = typeof BRIDGE_TOOLS[number];

/** The coarse scope each tool needs; the tab refines (text, full previews, return exports). */
export const BRIDGE_TOOL_SCOPES: Readonly<Record<BridgeTool, BridgeScope | null>> = Object.freeze({
  listDocuments: 'read:structure',
  getCapabilities: null,
  listCommands: 'read:structure',
  inspect: 'read:structure',
  getLayer: 'read:structure',
  apply: 'write:document',
  undo: 'write:document',
  redo: 'write:document',
  revertTransaction: 'write:document',
  renderPreview: 'read:preview',
  export: null,
  publish: null,
  newDocument: 'write:document',
  closeDocument: 'write:document',
  copyLayer: 'write:document',
});

export interface PairingGrants {
  /** Self-reported by the pairing CLI; shown as "reports itself as …". */
  client: string;
  scopes: BridgeScope[];
  /** Absolute folders exports may be written into. */
  folders: string[];
  /**
   * Absolute folders the agent may read image files from (`asset:import`,
   * `AssetInput.localPath`). Absent means none; pairings made before 0.2 have
   * no such field and compare equal to an empty list.
   */
  importFolders?: string[];
}

// ---------------------------------------------------------------------------
// Messages. Every JSON frame is one of these; anything else closes the socket.

export interface DaemonHello { type: 'hello'; protocolVersions: string[]; nonce: string }
export interface TabAuth {
  type: 'auth';
  /** 'pair' consumes the single-use pairing token; 'resume' uses a stored key. */
  mode: 'pair' | 'resume';
  protocolVersion: string;
  pairingId: string;
  tabId: string;
  /** base64url HMAC-SHA256(key, authMessage(nonce, tabId, origin)). */
  mac: string;
  /** base64url, 32 random bytes: the daemon must prove the key over it (mutual authentication). */
  tabNonce: string;
  /** 'pair' only: what the user saw and allowed. Must equal what the CLI asked for. */
  approved?: PairingGrants;
}
/**
 * `proof` = base64url HMAC-SHA256(key, daemonProofMessage(tabNonce, daemonNonce, tabId, origin)).
 * The tab verifies it before it treats the socket as the paired daemon: a process
 * that merely holds port 47651 cannot produce it. `grants` is informational; the
 * tab enforces the grants the user allowed (resume: the stored ones; pair: the
 * link's) and refuses a daemon that reports different ones.
 */
export interface DaemonAuthOk { type: 'auth-ok'; pairingId: string; grants: PairingGrants; proof: string }
export interface DaemonCall { type: 'call'; id: string; tool: BridgeTool; client: string; args: unknown }
export interface DaemonCancel { type: 'cancel'; id: string }
/**
 * Protocol 0.2. Announces the staged assets of call `id`, which follows as a
 * `call` message once every asset's bytes were sent as binary chunks
 * (`BlobHeader.staged`/`offset`, in order). The daemon sends it only to an
 * authenticated tab, only for a call it has registered as outstanding, and
 * only for `apply`.
 */
export interface DaemonStage { type: 'stage'; id: string; tool: BridgeTool; assets: StagedAssetManifest[] }
export interface TabResult { type: 'result'; id: string; result: unknown; blob?: BlobHeader }
/** Sent when the tab gains focus or its documents change, so default targeting and DocRefs stay current. */
/** `focused`: the tab is visible and has focus (the daemon stamps its own clock when true). */
export interface TabStatus { type: 'status'; focusedAt: number; documents: number; focused?: boolean }
/** The user revoked this pairing in the tab; the daemon forgets it. */
export interface TabRevoke { type: 'revoke'; pairingId: string }
export type DaemonToTab = DaemonHello | DaemonAuthOk | DaemonCall | DaemonCancel | DaemonStage;
export type TabToDaemon = TabAuth | TabResult | TabStatus | TabRevoke;

/** Close codes (4000–4999 are application codes). */
export const BRIDGE_CLOSE = Object.freeze({
  authTimeout: 4001,
  authFailed: 4003,
  protocol: 4400,
  duplicateTab: 4409,
  revoked: 4410,
  tooLarge: 1009,
});

// ---------------------------------------------------------------------------
// Binary frames: u32 big-endian header length, a JSON header, the payload.

/**
 * `id` is the call. Tab → daemon (previews, exports): `bytes` is the payload
 * length and `sha256` its digest. Daemon → tab (0.2, staged chunks): `staged`
 * names the asset, `offset` is where this chunk starts, `bytes` is the chunk
 * length and `sha256` the digest of the whole asset.
 */
export interface BlobHeader { id: string; mediaType: string; bytes: number; sha256: string; staged?: string; offset?: number }

export function encodeBinaryFrame(header: BlobHeader, payload: Uint8Array): Uint8Array {
  const head = new TextEncoder().encode(JSON.stringify(header));
  const frame = new Uint8Array(4 + head.byteLength + payload.byteLength);
  new DataView(frame.buffer).setUint32(0, head.byteLength, false);
  frame.set(head, 4);
  frame.set(payload, 4 + head.byteLength);
  return frame;
}

export function decodeBinaryFrame(frame: Uint8Array): { header: BlobHeader; payload: Uint8Array } | null {
  if (frame.byteLength < 4) return null;
  const length = new DataView(frame.buffer, frame.byteOffset, frame.byteLength).getUint32(0, false);
  if (length > 4096 || 4 + length > frame.byteLength) return null;
  let header: unknown;
  try { header = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(frame.subarray(4, 4 + length))); } catch { return null; }
  if (!header || typeof header !== 'object') return null;
  const record = header as Record<string, unknown>;
  if (typeof record.id !== 'string' || typeof record.mediaType !== 'string' || typeof record.sha256 !== 'string' || typeof record.bytes !== 'number') return null;
  if (record.staged !== undefined && typeof record.staged !== 'string') return null;
  if (record.offset !== undefined && (typeof record.offset !== 'number' || !Number.isSafeInteger(record.offset) || record.offset < 0)) return null;
  if ((record.staged === undefined) !== (record.offset === undefined)) return null;
  const payload = frame.subarray(4 + length);
  if (payload.byteLength !== record.bytes) return null;
  const decoded: BlobHeader = { id: record.id, mediaType: record.mediaType, bytes: record.bytes, sha256: record.sha256 };
  if (typeof record.staged === 'string' && typeof record.offset === 'number') { decoded.staged = record.staged; decoded.offset = record.offset; }
  return { header: decoded, payload };
}

// ---------------------------------------------------------------------------
// Encodings and authentication.

const PAIRING_ID = /^p_[A-Za-z0-9_-]{16,64}$/;
const TAB_ID = /^tab_[A-Za-z0-9_-]{8,64}$/;
const TOKEN = /^[A-Za-z0-9_-]{43}$/;

export const isPairingId = (value: unknown): value is string => typeof value === 'string' && PAIRING_ID.test(value);
export const isTabId = (value: unknown): value is string => typeof value === 'string' && TAB_ID.test(value);
export const isPairingToken = (value: unknown): value is string => typeof value === 'string' && TOKEN.test(value);

export function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(text: string): Uint8Array {
  const binary = atob(text.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (text.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

const encoder = new TextEncoder();
const HKDF_SALT = encoder.encode('maipo-agent-pairing/0.1');

/** HKDF info binds the key to the pairing and to the tab origin that allowed it. */
export function pairingKeyInfo(pairingId: string, origin: string): Uint8Array {
  return encoder.encode(`${pairingId}\n${origin}`);
}

/** The bytes the tab signs: nonce ‖ tabId ‖ 0x00 ‖ origin (length-unambiguous: the nonce is 32 bytes and tab ids hold no NUL). */
export function authMessage(nonce: Uint8Array, tabId: string, origin: string): Uint8Array {
  const tab = encoder.encode(tabId);
  const from = encoder.encode(origin);
  const message = new Uint8Array(nonce.byteLength + tab.byteLength + 1 + from.byteLength);
  message.set(nonce, 0);
  message.set(tab, nonce.byteLength);
  message[nonce.byteLength + tab.byteLength] = 0;
  message.set(from, nonce.byteLength + tab.byteLength + 1);
  return message;
}

function subtle(): SubtleCrypto {
  const value = globalThis.crypto?.subtle;
  if (!value) throw new Error('WebCrypto is unavailable.');
  return value;
}

/**
 * The pairing key as an HMAC-SHA256 CryptoKey. The tab asks for a
 * non-extractable key (stored in IndexedDB); the daemon asks for an
 * extractable one so it can persist the raw bytes in its 0600 store.
 */
export async function derivePairingKey(token: string, pairingId: string, origin: string, extractable: boolean): Promise<CryptoKey> {
  const base = await subtle().importKey('raw', fromBase64Url(token) as BufferSource, 'HKDF', false, ['deriveKey']);
  return subtle().deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: HKDF_SALT as BufferSource, info: pairingKeyInfo(pairingId, origin) as BufferSource },
    base,
    { name: 'HMAC', hash: 'SHA-256', length: 256 },
    extractable,
    ['sign', 'verify'],
  );
}

export function importPairingKey(raw: Uint8Array): Promise<CryptoKey> {
  return subtle().importKey('raw', raw as BufferSource, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign', 'verify']);
}

export async function exportPairingKey(key: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(await subtle().exportKey('raw', key));
}

const DAEMON_PROOF_LABEL = encoder.encode('maipo-bridge-daemon-proof/0.1\u0000');

/** What the daemon signs to prove it holds the pairing key: label ‖ tabNonce ‖ daemonNonce ‖ tabId ‖ 0x00 ‖ origin. */
export function daemonProofMessage(tabNonce: Uint8Array, daemonNonce: Uint8Array, tabId: string, origin: string): Uint8Array {
  const tail = authMessage(daemonNonce, tabId, origin);
  const message = new Uint8Array(DAEMON_PROOF_LABEL.byteLength + tabNonce.byteLength + tail.byteLength);
  message.set(DAEMON_PROOF_LABEL, 0);
  message.set(tabNonce, DAEMON_PROOF_LABEL.byteLength);
  message.set(tail, DAEMON_PROOF_LABEL.byteLength + tabNonce.byteLength);
  return message;
}

export async function signDaemonProof(key: CryptoKey, tabNonce: Uint8Array, daemonNonce: Uint8Array, tabId: string, origin: string): Promise<string> {
  return toBase64Url(new Uint8Array(await subtle().sign('HMAC', key, daemonProofMessage(tabNonce, daemonNonce, tabId, origin) as BufferSource)));
}

export function verifyDaemonProof(key: CryptoKey, tabNonce: Uint8Array, daemonNonce: Uint8Array, tabId: string, origin: string, proof: unknown): Promise<boolean> {
  if (typeof proof !== 'string') return Promise.resolve(false);
  let signature: Uint8Array;
  try { signature = fromBase64Url(proof); } catch { return Promise.resolve(false); }
  if (signature.byteLength !== 32) return Promise.resolve(false);
  return subtle().verify('HMAC', key, signature as BufferSource, daemonProofMessage(tabNonce, daemonNonce, tabId, origin) as BufferSource);
}

export async function signChallenge(key: CryptoKey, nonce: Uint8Array, tabId: string, origin: string): Promise<string> {
  return toBase64Url(new Uint8Array(await subtle().sign('HMAC', key, authMessage(nonce, tabId, origin) as BufferSource)));
}

/** Constant-time verification (WebCrypto's HMAC verify). */
export function verifyChallenge(key: CryptoKey, nonce: Uint8Array, tabId: string, origin: string, mac: string): Promise<boolean> {
  let signature: Uint8Array;
  try { signature = fromBase64Url(mac); } catch { return Promise.resolve(false); }
  if (signature.byteLength !== 32) return Promise.resolve(false);
  return subtle().verify('HMAC', key, signature as BufferSource, authMessage(nonce, tabId, origin) as BufferSource);
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  return [...new Uint8Array(await subtle().digest('SHA-256', bytes as BufferSource))].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

// ---------------------------------------------------------------------------
// Pairing links.

export interface PairingLink {
  token: string;
  pairingId: string;
  port: number;
  grants: PairingGrants;
}

const CLIENT_LABEL = /^[^\p{Cc}\u202a-\u202e\u2066-\u2069]{1,64}$/u;

export function normaliseScopes(values: readonly string[]): BridgeScope[] | null {
  const known = new Set<string>(BRIDGE_SCOPES);
  if (values.some((value) => !known.has(value))) return null;
  return BRIDGE_SCOPES.filter((scope) => values.includes(scope));
}

/** The fragment the daemon opens; parameters are URL-encoded, folders repeat. */
export function formatPairingFragment(link: PairingLink): string {
  const params = new URLSearchParams();
  params.set('agent-pair', link.token);
  params.set('port', String(link.port));
  params.set('pairing', link.pairingId);
  params.set('client', link.grants.client);
  params.set('scopes', link.grants.scopes.join(','));
  for (const folder of link.grants.folders) params.append('folder', folder);
  for (const folder of link.grants.importFolders ?? []) params.append('import', folder);
  return params.toString();
}

export type PairingLinkParse = { ok: true; link: PairingLink } | { ok: false; reason: string } | { ok: false; reason: 'absent' };

/** Parses a location hash (with or without '#'). Everything in it is untrusted until the daemon confirms it. */
export function parsePairingFragment(hash: string): PairingLinkParse {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  if (!params.has('agent-pair')) return { ok: false, reason: 'absent' };
  const token = params.get('agent-pair');
  const pairingId = params.get('pairing');
  const port = Number(params.get('port'));
  const client = params.get('client') ?? '';
  const scopes = normaliseScopes((params.get('scopes') ?? '').split(',').filter(Boolean));
  const folders = params.getAll('folder');
  const importFolders = params.getAll('import');
  if (!isPairingToken(token)) return { ok: false, reason: 'The pairing link has no valid token.' };
  if (!isPairingId(pairingId)) return { ok: false, reason: 'The pairing link has no valid pairing id.' };
  if (port !== BRIDGE_PORT) return { ok: false, reason: `The pairing link names port ${String(params.get('port'))}; Maipo only connects to ${BRIDGE_PORT}.` };
  if (!CLIENT_LABEL.test(client)) return { ok: false, reason: 'The pairing link has no valid client name.' };
  if (!scopes) return { ok: false, reason: 'The pairing link asks for an unknown permission.' };
  const invalidFolders = (list: string[]) => list.length > BRIDGE_LIMITS.maxFolders || list.some((folder) => !folder || folder.length > BRIDGE_LIMITS.maxFolderLength || /\p{Cc}/u.test(folder));
  if (invalidFolders(folders) || invalidFolders(importFolders)) return { ok: false, reason: 'The pairing link lists invalid folders.' };
  // asset:import without import folders (or folders without the scope) is not something `maipo-mcp pair` asks for.
  if (scopes.includes('asset:import') !== importFolders.length > 0) return { ok: false, reason: 'The pairing link\'s import permission and import folders do not match.' };
  return { ok: true, link: { token, pairingId, port, grants: { client, scopes, folders, ...(importFolders.length ? { importFolders } : {}) } } };
}

/** A structurally valid PairingGrants (untrusted input from either end). */
export function isPairingGrants(value: unknown): value is PairingGrants {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return typeof record.client === 'string' && record.client.length <= 64
    && Array.isArray(record.scopes) && record.scopes.length <= BRIDGE_SCOPES.length && record.scopes.every((scope) => typeof scope === 'string')
    && normaliseScopes(record.scopes) !== null
    && Array.isArray(record.folders) && record.folders.length <= BRIDGE_LIMITS.maxFolders && record.folders.every((folder) => typeof folder === 'string' && folder.length <= BRIDGE_LIMITS.maxFolderLength)
    && (record.importFolders === undefined || (Array.isArray(record.importFolders) && record.importFolders.length <= BRIDGE_LIMITS.maxFolders && record.importFolders.every((folder) => typeof folder === 'string' && folder.length <= BRIDGE_LIMITS.maxFolderLength)));
}

/** The import folders of a grant (none when the field is absent). */
export const importFoldersOf = (grants: PairingGrants): readonly string[] => grants.importFolders ?? [];

/** Exact equality; false (never a throw) for malformed input. */
export function sameGrants(left: unknown, right: unknown): boolean {
  if (!isPairingGrants(left) || !isPairingGrants(right)) return false;
  return left.client === right.client
    && left.scopes.length === right.scopes.length && left.scopes.every((scope, index) => right.scopes[index] === scope)
    && left.folders.length === right.folders.length && left.folders.every((folder, index) => right.folders[index] === folder)
    && importFoldersOf(left).length === importFoldersOf(right).length && importFoldersOf(left).every((folder, index) => importFoldersOf(right)[index] === folder);
}
